import {
  email,
  required,
  minLength,
  maxLength,
} from 'vuelidate/lib/validators';
import flow from 'lodash/flow';
import { iterateReplacementToString } from '@/helpers/index';

interface IContractorForm {
  type: string;
}

export const enum ContractorTypes {
  person = 'person',
  entrepreneur = 'entrepreneur',
  company = 'company',
}

export const GENDER_OPTIONS = [
  { label: 'Мужской', gender: 'M' },
  { label: 'Женский', gender: 'F' },
];

export const OPTIONS = [
  { name: 'Физическое лицо', category: ContractorTypes.person },
  {
    name: 'Индивидуальный предприниматель',
    category: ContractorTypes.entrepreneur,
  },
  { name: 'Юридическое лицо', category: ContractorTypes.company },
];

export const OPTIONS_WITHOUT_PERSON = [
  {
    name: 'Индивидуальный предприниматель',
    category: ContractorTypes.entrepreneur,
  },
  { name: 'Юридическое лицо', category: ContractorTypes.company },
];

export const FORM_DATA_COMPANY = () => ({
  type: ContractorTypes.company,
  inn: '',
  emails: [],
  phone: '',
  name: '',
  shortName: '',
  alias: '',
  kpp: '',
  ogrn: '',
  okpo: '',
  okved: '',
  addressLegal: '',
  addressFact: '',
  addressPostal: '',
  bankRequisites: {
    bik: '',
    bankName: '',
    account: '',
    correspondentAccount: '',
  },
});

export const FORM_DATA_PERSON = () => ({
  type: ContractorTypes.person,
  emails: [],
  phone: '',
  firstName: '',
  lastName: '',
  middleName: '',
});

export const FORM_DATA_ENTREPRENEUR = () => ({
  type: ContractorTypes.entrepreneur,
  inn: '',
  emails: [],
  phone: '',
  name: '',
  shortName: '',
  alias: '',
  ogrn: '',
  okpo: '',
  okved: '',
  addressLegal: '',
  addressFact: '',
  addressPostal: '',
  bankRequisites: {
    bik: '',
    bankName: '',
    account: '',
    correspondentAccount: '',
  },
});

export const MOCK_EMPLOYEES = [
  {
    uuid: '41c8d33f-6113-467c-99b6-3e17c35e3509',
    firstName: 'Иван',
    lastName: 'Иванов',
    middleName: 'Иванович',
    gender: 'M',
    isSigner: true,
    position: {
      name: 'Директор',
      activeFrom: '2020-02-10',
      isHead: true,
      credentials: {
        type: 'Доверенность',
        number: '123/ТТ',
        date: '2020-01-16',
      },
    },
  },
  {
    uuid: '41c8d33f-6113-467c-99b6-3e17c35e4954',
    firstName: 'Лариса',
    lastName: 'Кузнецова',
    middleName: 'Петровна',
    gender: 'F',
    isSigner: false,
    position: {
      name: 'Бухгалтер',
      activeFrom: '2020-02-10',
      isHead: true,
      credentials: {
        type: 'Доверенность',
        number: '123/ТТ',
        date: '2020-01-16',
      },
    },
  },
  {
    uuid: '41c8d33f-6113-467c-99b6-3e17c35e9845',
    firstName: 'Петр',
    lastName: 'Петров',
    middleName: 'Петрович',
    gender: 'M',
    isSigner: false,
    position: {
      name: 'Водитель',
      activeFrom: '2020-02-10',
      isHead: true,
      credentials: {
        type: 'Доверенность',
        number: '123/ТТ',
        date: '2020-01-16',
      },
    },
  },
];

export const VALIDATION_COMPANY = () => ({
  form: {
    inn: { required, minLength: minLength(10), maxLength: maxLength(10) },
    name: { required },
    phone: { minLength: minLength(18), required },
    emails: {
      required,
      $each: {
        email,
      },
    },
    kpp: { required, minLength: minLength(9), maxLength: maxLength(9) },
    ogrn: { minLength: minLength(13), maxLength: maxLength(13) },
    okpo: { minLength: minLength(8), maxLength: maxLength(8) },
    bankRequisites: {
      bik: { minLength: minLength(9), maxLength: maxLength(9) },
      account: { minLength: minLength(20), maxLength: maxLength(20) },
      correspondentAccount: {
        minLength: minLength(20),
        maxLength: maxLength(20),
      },
    },
  },
});

export const VALIDATION_ENTREPRENEUR = () => ({
  form: {
    inn: { required, minLength: minLength(12), maxLength: maxLength(12) },
    name: { required },
    phone: { minLength: minLength(18), required },
    emails: {
      required,
      $each: {
        email,
      },
    },
    ogrn: { minLength: minLength(15), maxLength: maxLength(15) },
    okpo: { minLength: minLength(10), maxLength: maxLength(10) },
    bankRequisites: {
      bik: { minLength: minLength(9), maxLength: maxLength(9) },
      account: { minLength: minLength(20), maxLength: maxLength(20) },
      correspondentAccount: {
        minLength: minLength(20),
        maxLength: maxLength(20),
      },
    },
  },
});

export const VALIDATION_COMPANY_IS_EDIT = () => ({
  form: {
    inn: { required },
    name: { required },
    phone: { minLength: minLength(18), required },
    emails: {
      required,
      $each: {
        email,
      },
    },
    kpp: { required, minLength: minLength(9), maxLength: maxLength(9) },
    ogrn: { minLength: minLength(13), maxLength: maxLength(13) },
    okpo: { minLength: minLength(8), maxLength: maxLength(8) },
    bankRequisites: {
      bik: { minLength: minLength(9), maxLength: maxLength(9) },
      account: { minLength: minLength(20), maxLength: maxLength(20) },
      correspondentAccount: {
        minLength: minLength(20),
        maxLength: maxLength(20),
      },
    },
  },
});

export const VALIDATION_ENTREPRENEUR_IS_EDIT = () => ({
  form: {
    inn: { required },
    name: { required },
    phone: { minLength: minLength(18), required },
    emails: {
      required,
      $each: {
        email,
      },
    },
    ogrn: { minLength: minLength(15), maxLength: maxLength(15) },
    okpo: { minLength: minLength(10), maxLength: maxLength(10) },
    bankRequisites: {
      bik: { minLength: minLength(9), maxLength: maxLength(9) },
      account: { minLength: minLength(20), maxLength: maxLength(20) },
      correspondentAccount: {
        minLength: minLength(20),
        maxLength: maxLength(20),
      },
    },
  },
});

export const VALIDATION_PERSON = () => ({
  form: {
    firstName: { required },
    lastName: { required },
    phone: { minLength: minLength(18), required },
    emails: {
      required,
      $each: {
        email,
      },
    },
  },
});

export function formattedContractorForForm(contractor: IContractorForm) {
  let form: IContractorForm = { type: '' };
  switch (contractor.type) {
    case ContractorTypes.person:
      form = FORM_DATA_PERSON();
      break;
    case ContractorTypes.company:
      form = FORM_DATA_COMPANY();
      break;
    case ContractorTypes.entrepreneur:
      form = FORM_DATA_ENTREPRENEUR();
      break;
    default:
      break;
  }
  const filteredContractor = flow([
    Object.entries,
    (arr) => arr.filter(([key, value]: string) => value && key in form),
    Object.fromEntries,
  ])(contractor);
  iterateReplacementToString(filteredContractor);
  Object.assign(form, filteredContractor);
  return form;
}

/**
 * Отображение типа контрагента
 * @param {String} type - тип контрагента
 * @returns {String}
 */
export const getTypeContractor = (type: string): string => {
  switch (type) {
    case 'person':
      return 'ФЛ';
      break;
    case 'company':
      return 'ЮЛ';
      break;
    case 'entrepreneur':
      return 'ИП';
      break;
    default:
      return '';
      break;
  }
};
